import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  CSSProperties,
} from "react";
import Quill, { QuillOptions } from "quill";
import { Box, CircularProgress } from "@mui/material";
import "quill/dist/quill.snow.css";
import Block from "quill/blots/block";

class DivBlock extends Block {}
DivBlock.blotName = "div";
DivBlock.tagName = "div";
Quill.register(DivBlock, true);

export interface EmailPreviewEditorRef {
  quill: Quill | null;
  setEmailHTMLContent: (content: string) => void;
  getEmailHTMLContent: () => string;
  enable: (enabled: boolean) => void;
}

export interface EmailPreviewEditorProps {
  style?: CSSProperties;
  defaultValue?: string;
  readOnly?: boolean;
  loading?: boolean;
}

const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ header: 1 }, { header: 2 }],
  [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ color: [] }, { background: [] }],
  ["clean"],
];

function createQuill(container: HTMLDivElement) {
  const options: QuillOptions = {
    theme: "snow",
    modules: {
      toolbar: toolbarOptions,
      clipboard: {
        matchers: [
          ["img", () => null],
          ["video", () => null],
        ],
      },
    },
  };
  const quill = new Quill(container, options);

  quill.root.addEventListener("paste", (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file" && /^image\/|^video\//.test(item.type)) {
        e.preventDefault();
        return;
      }
      if (item.kind === "string" && item.type === "text/html") {
        item.getAsString((html: any) => {
          if (/<img|<video/.test(html)) {
            e.preventDefault();
          }
        });
      }
    }
  });

  return quill;
}

const EmailPreviewEditor = forwardRef<
  EmailPreviewEditorRef,
  EmailPreviewEditorProps
>(({ style = {}, defaultValue, readOnly = false, loading = false }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);

  useEffect(() => {
    const container = containerRef.current!;
    const editorContainer = container.appendChild(
      container.ownerDocument.createElement("div")
    );

    const quill = createQuill(editorContainer);
    quillRef.current = quill;

    if (defaultValue) {
      quill.clipboard.dangerouslyPasteHTML(defaultValue);
    }

    return () => {
      container.innerHTML = "";
      quillRef.current = null;
    };
  }, [defaultValue]);

  useEffect(() => {
    quillRef.current?.enable(!readOnly);
  }, [readOnly]);

  useImperativeHandle(
    ref,
    () => ({
      quill: quillRef.current,
      setEmailHTMLContent: (content: string) => {
        quillRef.current?.clipboard.dangerouslyPasteHTML(content);
      },
      getEmailHTMLContent: () => quillRef.current?.root.innerHTML || "",
      enable: (enabled: boolean) => {
        quillRef.current?.enable(enabled);
      },
    }),
    [quillRef.current]
  );

  return (
    <Box sx={{ position: "relative" }}>
      <div ref={containerRef} style={style}></div>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size="1rem" />
        </Box>
      )}
    </Box>
  );
});

export default EmailPreviewEditor;
